import React, { useEffect, useRef, useState } from 'react';

/**
 * BopMultiSelect — combo a scelta multipla per le barre filtri.
 * Mostra un riassunto delle voci scelte; aperto, elenca le voci con una
 * casella ciascuna più "Tutti" / "Nessuno". Selezione vuota = nessun
 * filtro (etichetta `tuttiLabel`). Nessuna dipendenza esterna.
 */

export interface MultiSelectItem {
  id: string;
  nome: string;
}

interface Props {
  label: string;          // usato come aria-label
  tuttiLabel: string;     // es. "Tutti gli stati", mostrato quando nulla è selezionato
  items: MultiSelectItem[];
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}

export default function BopMultiSelect({ label, tuttiLabel, items, value, onChange, disabled = false }: Props) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const chiudi = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', chiudi);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', chiudi); document.removeEventListener('keydown', esc); };
  }, [open]);

  const selezionati = items.filter(i => value.includes(i.id));
  const riassunto = selezionati.length === 0
    ? tuttiLabel
    : selezionati.length === items.length
      ? tuttiLabel
      : selezionati.map(i => i.nome).join(', ');

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);
  };

  return (
    <div ref={wrapperRef} className={`bop-multiselect${open ? ' bop-multiselect--aperto' : ''}`}>
      <button
        type="button"
        className="bop-select bop-multiselect__pulsante"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        title={riassunto}
      >
        <span className="bop-multiselect__testo">{riassunto}</span>
        {selezionati.length > 0 && selezionati.length < items.length && (
          <span className="bop-multiselect__conteggio">{selezionati.length}</span>
        )}
      </button>
      {open && (
        <div className="bop-multiselect__menu" role="listbox" aria-multiselectable="true">
          <div className="bop-multiselect__azioni">
            <button type="button" className="bop-multiselect__azione" onClick={() => onChange(items.map(i => i.id))}>Tutti</button>
            <button type="button" className="bop-multiselect__azione" onClick={() => onChange([])}>Nessuno</button>
          </div>
          {items.map(i => (
            <label key={i.id} className="bop-multiselect__voce" role="option" aria-selected={value.includes(i.id)}>
              <input type="checkbox" className="bop-multiselect__check" checked={value.includes(i.id)} onChange={() => toggle(i.id)} />
              <span className="bop-multiselect__nome">{i.nome}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
